import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { AsyncInit, Result } from '@unimarket/shared';

import { Cart } from '../model/cart';
import { Checkout } from '../model/checkout';
import { PlacedOrderPage } from '../model/placed-order-page';
import { WayforpayResponse } from '../model/wayforpay';
import { ApiService } from './api.service';

/** What the header shows until unimarket-api answers; the total it sends carries the currency. */
const EMPTY: Cart = { products: [], total: { value: 0 } };

/** When the API refused without saying why, or could not be reached at all. */
const REFUSED = 'Не вдалося оновити кошик. Спробуйте ще раз.';

/**
 * The API's own words, translated, out of `ApiExceptionConfig.ErrorResponse`. Anything else -
 * a network failure, a gateway's own error page - has no message worth showing.
 */
function refusal(error: unknown): string {
  const body = (error as { error?: { message?: string } } | null)?.error;
  return body?.message || REFUSED;
}

@Injectable({ providedIn: 'root' })
export class CartService implements AsyncInit {

  private api = inject(ApiService);
  private state = signal<Cart>(EMPTY);

  readonly cart = this.state.asReadonly();

  async init(): Promise<void> {
    try {
      this.state.set(await firstValueFrom(this.api.get<Cart>('/cart')));
    } catch (error) {
      console.error('Cannot load the cart from unimarket-api', error);
    }
  }

  add(productId: number, quantity: number): Promise<Result> {
    return this.change(this.api.post<Cart>(`/cart/products/${productId}?quantity=${quantity}`));
  }

  remove(productId: number): Promise<Result> {
    return this.change(this.api.delete<Cart>(`/cart/products/${productId}`));
  }

  changeQuantities(quantities: Record<number, number>): Promise<Result> {
    return this.change(this.api.put<Cart>('/cart/products', quantities));
  }

  placeOrder(checkout: Checkout): Promise<PlacedOrderPage> {
    return firstValueFrom(this.api.post<PlacedOrderPage>('/orders', checkout));
  }

  confirmPayment(payment: WayforpayResponse): Promise<unknown> {
    return firstValueFrom(this.api.post<unknown>('/orders/payment', payment));
  }

  private async change(call: Observable<Cart>): Promise<Result> {
    try {
      this.state.set(await firstValueFrom(call));
      return Result.ok();
    } catch (error) {
      await this.init();
      return Result.error(refusal(error));
    }
  }

}
